import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowRight, BookOpen, FlaskConical, Gift, Grid2X2, Leaf, Pill, Sparkles, Stethoscope,
} from "lucide-react";
import { fetchProducts } from "../lib/api";
import { useCollections, matchesCollection } from "../lib/useCollections";
import { EbookCard, PackCard, ProductCard, SectionHead } from "../components/ShopCards";
import { usePageSeo } from "../lib/usePageSeo";
import { crumbsJsonLd } from "../lib/seoHead";
import TrustStrip from "../components/TrustStrip";

export const COLLECTION_ICONS = {
    grid: Grid2X2, pill: Pill, book: BookOpen, stethoscope: Stethoscope,
    sparkles: Sparkles, leaf: Leaf, gift: Gift, flask: FlaskConical,
};

export const ItemCard = ({ p }) => {
    const type = p.shop_type || "product";
    if (type === "ebook") return <EbookCard p={p} />;
    if (type === "consultation_pack") return <PackCard p={p} />;
    return <ProductCard p={p} />;
};

const CollectionCard = ({ c, count }) => {
    const Icon = COLLECTION_ICONS[c.icon] || Grid2X2;
    return (
        <Link to={`/shop/${c.slug}`} data-testid={`collection-card-${c.slug}`}
            className="group relative flex flex-col border border-white/10 bg-bg1 overflow-hidden hover:border-gold/50 hover:-translate-y-1 transition-all">
            {c.image_url && (
                <div className="aspect-[16/9] overflow-hidden">
                    <img src={c.image_url} alt={c.name} loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
            )}
            <div className="p-7 flex flex-col flex-1">
                <Icon className="w-7 h-7 text-gold" />
                <h2 className="font-display text-2xl md:text-[28px] uppercase tracking-tight mt-5">{c.name}</h2>
                {c.tagline && <p className="text-sm text-zinc-500 mt-2.5 leading-relaxed">{c.tagline}</p>}
                <div className="mt-auto pt-6 flex items-center justify-between gap-3">
                    <span className="mono text-[9px] tracking-[0.2em] text-zinc-500"
                        data-testid={`collection-count-${c.slug}`}>
                        {count} {count === 1 ? "ITEM" : "ITEMS"}
                    </span>
                    <span className="mono text-[10px] tracking-[0.18em] text-lime inline-flex items-center gap-1.5">
                        EXPLORE <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                </div>
            </div>
        </Link>
    );
};

export default function Shop() {
    const { data: all = [], isLoading } = useQuery({
        queryKey: ["products"], queryFn: fetchProducts, staleTime: 15000, refetchOnMount: "always",
    });
    const { collections, hub, sections, isLoading: loadingCollections } = useCollections();

    const visible = all.filter((p) => {
        const map = { product: sections.products, ebook: sections.ebooks, consultation_pack: sections.consultation_packs };
        return map[p.shop_type || "product"] !== false;
    });
    // An empty collection card leads to an empty page — so only show what has stock to show.
    const withCounts = collections
        .map((c) => ({ c, count: visible.filter((p) => matchesCollection(p, c, sections)).length }))
        .filter((x) => x.count > 0);
    const featured = hub.featured_slugs?.length
        ? hub.featured_slugs.map((s) => visible.find((p) => p.slug === s)).filter(Boolean)
        : visible.filter((p) => p.purchasable !== false).slice(0, 4);

    usePageSeo({
        title: "Shop — Nutraceuticals, Guides & Consultations | ReHealthMe",
        description: hub.sub || "Effervescent nutraceuticals, readable health guides and one-to-one consultation packs from ReHealthMe.",
        canonical: `${window.location.origin}/shop`,
        jsonLd: [crumbsJsonLd([{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }])],
    });

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid="shop-hub">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="max-w-2xl">
                    <div className="eyebrow mb-3">{hub.eyebrow}</div>
                    <h1 className="font-display text-5xl md:text-6xl uppercase tracking-tight">{hub.heading}</h1>
                    {hub.sub && <p className="mt-5 text-zinc-500">{hub.sub}</p>}
                </div>
                <TrustStrip className="mt-8" />

                {loadingCollections || isLoading ? (
                    <div className="mt-12 text-zinc-500">Loading…</div>
                ) : (
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="collection-grid">
                        {withCounts.map(({ c, count }) => (
                            <CollectionCard key={c.id} c={c} count={count} />
                        ))}
                    </div>
                )}

                {!withCounts.length && !loadingCollections && !isLoading && (
                    <div className="mt-12 border border-white/10 bg-bg1 p-12 text-center" data-testid="shop-empty">
                        <div className="font-display text-2xl uppercase tracking-tight">Nothing listed yet</div>
                        <p className="text-sm text-zinc-500 mt-2">New products and guides are on the way.</p>
                    </div>
                )}

                {hub.featured_enabled && !isLoading && !!featured.length && (
                    <section className="mt-24" data-testid="shop-featured">
                        <SectionHead eyebrow="Hand-picked" title={hub.featured_title || "New & noteworthy"}
                            action={<Link to="/shop/all-products" data-testid="shop-featured-all"
                                className="mono text-[10px] tracking-[0.18em] text-gold hover:underline">
                                SEE EVERYTHING →
                            </Link>} />
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="shop-featured-grid">
                            {featured.map((p) => <ItemCard key={p.id} p={p} />)}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
